import React from "react";
import { parseBoolean } from "../constants/utils";
import AppliedFilters from "./AppliedFilters";
import ArticleListing from "./ArticleListing";
import GenericFilter from "./GenericFilter";
import SeriesData from "./SeriesData";
import SeriesListing from "./SeriesListing";
import Spinner from "./Spinner";
import UrlFilterHelper from "./UrlFilterHelper";
import FootNoteData from "./FootNoteData";
import { updateProductGroupVariants } from "./SchemaOrgGraph";

export default function ProductArticles(props) {
    let hideSeries = parseBoolean(props.hideSeries);
    let filterHelper = new UrlFilterHelper();
    let selectedSeries = null;
    let articlesUrl = i18n.baseUrl + "api/articlefilter/search";

    const [showResultBar, setShowResultBar] = React.useState(false);
    const [loading, setLoading] = React.useState("");
    const [series, setSeries] = React.useState([]);
    const [articles, setArticles] = React.useState([]);
    const [ancestorIds, setAncestorIds] = React.useState([]);
    const [additionalTableColumns, setAdditionalTableColumns] = React.useState([]);
    const [filterGroups, setFilterGroups] = React.useState([]);
    const [totalMatching, setTotalMatching] = React.useState(0);
    const [showInlineSeriesData, setShowInlineSeriesData] = React.useState(false);
    const [seriesDataSelectedSeries, setSeriesDataSelectedSeries] = React.useState(null);
    const [error, setError] = React.useState(false);

    React.useEffect(() => {
        const tabBtns = document.querySelectorAll("#product-tabs li a");
        tabBtns.forEach((btn) => {
            btn.addEventListener("click", handleTabClick);
        });
        setActiveTab();
    }, []);

    const handleTabClick = async (e) => {
        let oldScrollPosition = window.scrollY;
        window.location.hash = e.target.getAttribute("data-pane-id");
        window.scrollTo({ top: oldScrollPosition });

        if (e.target.id === "product-tab2") {
            await getArticles(filterHelper.getFiltersFromUrl());
        }
    };

    const setCode = async () => {
        filterHelper.setQueryStringValue("code", props.productCode);
    };

    const getArticles = async (filters) => {
        await setCode();

        let _filters = filters;

        // apply session filters if it exists
        let storedFilters = JSON.parse(sessionStorage.getItem("storedFilters"));
        if (Array.isArray(storedFilters) && storedFilters.length > 0) {
            filterHelper.setFilters(_filters ?? []);
            _filters = storedFilters;
            sessionStorage.removeItem("storedFilters");
        }

        setLoading("loadingData");
        let apiResponseTimer = performance.now();

        try {
            let resp = await fetch(articlesUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    code: props.productCode,
                    selectedFilters: _filters ?? [],
                    pageSize: 1000,
                }),
            });

            if (resp.ok) {
                let respJson = await resp.json();

                // Send event to analytics
                try {
                    apiResponseTimer = Math.round(performance.now() - apiResponseTimer);
                    console.log("Product api " + apiResponseTimer);

                    dataLayer.push({
                        event: "cejn.event",
                        eventCategory: "product-filter-performance-api",
                        eventAction: window.location.pathname,
                        eventLable: apiResponseTimer,
                    });
                } catch (e) {
                    console.log(e);
                }

                updateProductGroupVariants(respJson.items, props.productCode, respJson.series);
                setArticles(respJson.items);

                // Check for and set additionalTableColumns
                if (respJson.additionalTableColumns.length) {
                    setAdditionalTableColumns(respJson.additionalTableColumns);
                }

                // Check for and set series
                if (respJson.series.length) {
                    let params = new URLSearchParams(window.location.search);

                    let filterparam = params.get("filters");
                    if (filterparam?.indexOf("SeriesCodes") > -1) {
                        respJson.series.forEach((serie) => {
                            if (filterparam.indexOf(serie.code) > -1) {
                                serie.selected = true;
                            }
                        });
                    }

                    // create local set 
                    let ancestorIds = new Set();
                    respJson.items.forEach((article) => {
                        article.ancestorNodes.forEach((id) => {
                            ancestorIds.add(id);
                        });
                    });

                    setAncestorIds(Array.from(ancestorIds));
                    setSeries(respJson.series);

                    // Check if we only have 1 series
                    // and set it as active.
                    if (respJson.series.length === 1) {
                        selectedSeries = respJson.series[0];
                        setSeriesDataSelectedSeries(respJson.series[0]);
                        setShowInlineSeriesData(true);
                    }
                }

                filterHelper.setFilters(respJson.filters ?? []);
                setFilterGroups(respJson.filters);

                setTotalMatching(Number(respJson.totalMatching) ?? 0);
                setShowResultBar(true);
                setLoading("");

                return respJson.filters;
            }
        } catch (error) {
            console.log(error);
            setLoading("");
            setError(true);
        }
    };

    const getAppliedFilterGroups = (filterGroups) => {
        let appliedFilters = [];

        filterGroups.forEach((filterGroup) => {
            if (filterGroup.selectedMin && filterGroup.selectedMax) {
                let filterGroupCopy = {
                    id: filterGroup.id,
                    selectedMin: filterGroup.selectedMin + "",
                    selectedMax: filterGroup.selectedMax + "",
                    text: filterGroup.text,
                    type: filterGroup.type,
                };
                appliedFilters.push(filterGroupCopy);
            }

            let selectedItems = filterGroup.items.filter((item) => item.selected);
            if (selectedItems.length) {
                // Create a copy so we don't mess with the filters applied
                let filterGroupCopy = {};
                Object.assign(filterGroupCopy, filterGroup);
                filterGroupCopy.items = selectedItems;
                appliedFilters.push(filterGroupCopy);
            }
        });

        return appliedFilters;
    };

    const setActiveTab = async () => {

        // Get first tab, if data-pane-id = "parts" show products tab
        let firstTabLink = document.querySelector("#product-tabs li a");
        if (firstTabLink.getAttribute("data-pane-id") === "parts") {
            firstTabLink.click();
            return false;
        }

        // Get hash without #
        let hash = window.location.hash.substring(1);

        // Get Code from querystring if it contains value and hash has no value
        const urlCode = new URLSearchParams(window.location.search).get("code");
        if (urlCode !== null && !hash) {
            let tabBtn = document.querySelector('a[data-pane-id="parts"]');
            tabBtn.click();
            return false;
        }

        // if hash value matches tab-content id then show tab-content
        let tabBtn = document.querySelector('a[data-pane-id="' + hash + '"]');
        if (tabBtn) {
            tabBtn.click();
            return false;
        }
    };

    const handleFilterSelected = async (clearSeries) => {
        setShowInlineSeriesData(false);
        let currentSelectedSeries = filterGroups.find(x => x.id === "SeriesCodes")?.items?.find(x => x.selected === true);

        if (selectedSeries) {
            if (currentSelectedSeries && currentSelectedSeries.text !== selectedSeries.code) {
                let filterString = filterHelper.setFilters([]);
                articlesUrl = i18n.baseUrl + "api/articlefilter/search?filters=" + filterString;

                // Fetch articles and update state manually
                let updatedFilters = await getArticles([]);
                if (updatedFilters) {
                    let updatedFilterSeries = updatedFilters.find((x) => x.id === "SeriesCodes");
                    let match = updatedFilterSeries.items.find((x) => x.text === selectedSeries.code);
                    if (!match) {
                        return;
                    }

                    match.selected = true;

                    let appliedFilterGroups = getAppliedFilterGroups(updatedFilters);
                    let filterString = filterHelper.setFilters(appliedFilterGroups);
                    articlesUrl = i18n.baseUrl + "api/articlefilter/search?filters=" + filterString;

                    await getArticles(appliedFilterGroups);
                }

                return;
            } else {

                if (currentSelectedSeries && currentSelectedSeries.text === selectedSeries.code) {
                    // clear selected series
                    console.log("same series clicked, clear it ")
                    selectedSeries = null;
                }
            }

            let filterSeries = filterGroups.find((x) => x.id === "SeriesCodes");
            if (!filterSeries || filterSeries.items.length === 0) {
                return;
            }

            filterSeries?.items?.forEach((item) => {
                item.selected = false;
            });

            if (!clearSeries && selectedSeries) {
                let match = filterSeries.items.filter((x) => x.text === selectedSeries.code)[0];
                if (!match) {
                    return;
                }
                match.selected = true;
            } else {
                selectedSeries = null;
            }
        }

        let appliedFilterGroups = getAppliedFilterGroups(filterGroups);
        let filterString = filterHelper.setFilters(appliedFilterGroups);
        articlesUrl = i18n.baseUrl + "api/articlefilter/search?filters=" + filterString;

        await getArticles(appliedFilterGroups);
    };

    const handleSeriesSelected = async (_selectedSeries) => {
        selectedSeries = _selectedSeries;
        await handleFilterSelected(false);
    };

    const showHideSeriesData = (series) => {
        if (series === seriesDataSelectedSeries && showInlineSeriesData === true) {
            setShowInlineSeriesData(false);
        } else {
            setSeriesDataSelectedSeries(series);
            setShowInlineSeriesData(true);
        }
    };

    if (error) {
        return <p>{i18n.getString("common.error")}</p>;
    }

    return (
        <>
            <div id="article-listing-element">
                {/* Series */}
                {!hideSeries && series?.length > 1 && (
                    <SeriesListing
                        ancestorIds={ancestorIds}
                        showInlineSeriesData={showInlineSeriesData ? true : false}
                        onItemSelected={handleSeriesSelected}
                        items={series}
                        filterGroups={filterGroups}
                        showHideSeriesData={showHideSeriesData}
                        seriesDataSelectedSeries={seriesDataSelectedSeries}
                    />
                )}

                {/* Selected Series Data */}
                {!hideSeries && seriesDataSelectedSeries !== null && showInlineSeriesData && (
                    <SeriesData
                        selectedSeries={series.length === 1 ? series[0] : seriesDataSelectedSeries}
                        hideButtons={series.length === 1}
                        isSingleSeries={series.length === 1 ? true : false}
                        handleClose={() => setShowInlineSeriesData(false)}
                    />
                )}

                {/* Filters */}
                {filterGroups?.length > 0 && (
                    <div className="c-product-filter row mt-5">
                        <GenericFilter onFilterChanged={handleFilterSelected} filterGroups={filterGroups} articles={articles} />
                    </div>
                )}

                {/* Applied Filters */}
                <AppliedFilters
                    series={series}
                    filterGroups={filterGroups}
                    onCleared={() => handleFilterSelected(true)}
                    onFilterRemoved={() => handleFilterSelected(true)}
                />

                {loading === "loadingData" && <Spinner />}

                {/* Articles */}
                {articles?.length > 0 && (
                    <>
                        <ArticleListing
                            onArticleFilterSelected={handleFilterSelected}
                            articles={articles}
                            additionalTableColumns={additionalTableColumns}
                            filterGroups={filterGroups}
                            totalMatching={totalMatching}
                            selectedSeries={""}
                            series={series}
                            showResultBar={showResultBar}
                            footNoteData={props.footNoteData}
                        />
                        {/* FootNoteData */}
                        {props.footNoteData !== "" && <FootNoteData footNoteData={props.footNoteData} />}
                    </>
                )}
            </div>
        </>
    );
}
